import { useState, useEffect } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import type { Banner, ListBannersResponse } from '../../../../apps/hooks/banner/useBanner';
import { useBanner } from '../../../../apps/hooks/banner/useBanner';
import { BannerList, BannerFilters, MediaPreviewModal, EditBannerModal, BannerOrderModal } from './components';

export default function BannerListPage() {
  // State for banners and pagination
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>(undefined);

  // State for filtering
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // State for edit modal
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // State for preview modal
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // State for order modal
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  // State for image load errors
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});

  const banner = useBanner();

  // Helper function to prepare banner data for API
  const prepareBannerForApi = (bannerData: Banner) => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      bannerId: _bannerId,
      date: _date,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      active: _active,
      ...apiData
    } = bannerData;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    return apiData;
  };

  // Handle order management
  const handleManageOrder = () => {
    setShowOrderModal(true);
  };

  const handleSaveOrder = async (orderUpdates: Array<{ bannerId: string; order: number }>) => {
    setIsUpdatingOrder(true);
    try {
      await banner.updateBannerOrders(orderUpdates);
      await fetchBanners(); // Refresh banners to show new order
      setShowOrderModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update banner order');
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  // Load banners when showActiveOnly changes
  useEffect(() => {
    console.log(`Fetching banners with active filter: ${showActiveOnly}`);
    fetchBanners();
  }, [showActiveOnly]);

  // Monitor banners state changes
  useEffect(() => {
    console.log('Banners state updated:', banners);
    console.log('Current banners length:', banners.length);
  }, [banners]);

  // Handle image preview
  const handleImagePreview = (
    imageUrl: string | undefined,
    title: string,
    device: 'desktop' | 'tablet' | 'mobile' = 'desktop',
  ) => {
    if (!imageUrl) {
      return;
    }

    setPreviewImage(imageUrl);
    setPreviewTitle(title + (device !== 'desktop' ? ` (${device.charAt(0).toUpperCase() + device.slice(1)})` : ''));
    setShowPreviewModal(true);
  };

  // Close preview modal
  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewImage(null);
  };

  // Fetch banners from API
  const fetchBanners = async (nextKey?: string) => {
    try {
      setLoading(true);
      console.log(`Fetching banners... Active only: ${showActiveOnly}`);

      const params: Record<string, any> = {
        limit: 100, // Set default limit to 100 banners
      };

      // Add active filter to params if we're showing only active banners
      if (showActiveOnly) {
        params['active'] = true;
        console.log('Added active=true filter to request');
      }

      if (nextKey) {
        params['lastEvaluatedKey'] = nextKey;
      }

      console.log('Request params:', params);
      const response: ListBannersResponse = await banner.listBanners(params);

      // Access the banners array from the response structure
      if (response && response.banners) {
        console.log(`Received ${response.banners.length} banners from server`);
        // Sort banners by order for consistent display
        const sortedBanners = [...response.banners].sort((a, b) => a.order - b.order);
        setBanners(sortedBanners);
        setLastEvaluatedKey(response.lastEvaluatedKey);
      } else {
        console.error('Response is missing banners array:', response);
        setBanners([]);
      }
    } catch (err) {
      console.error('Error fetching banners:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle banner deletion
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) {
      return;
    }

    try {
      await banner.deleteBanner(id);
      setBanners((prev) => prev.filter((banner) => banner.bannerId !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // Handle banner edit
  const handleEdit = (bannerToEdit: Banner) => {
    setEditingBanner({ ...bannerToEdit });
    setShowEditModal(true);
  };

  // Close edit modal
  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingBanner(null);
  };

  // Save edited banner
  const handleSaveEdit = async () => {
    if (!editingBanner) {
      return;
    }

    try {
      // Prepare the banner data for the API
      const bannerData = prepareBannerForApi(editingBanner);

      await banner.updateBanner(editingBanner.bannerId, bannerData);
      await fetchBanners();

      setShowEditModal(false);
      setEditingBanner(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update banner');
    }
  };

  // Handle form input changes
  const handleInputChange: (field: keyof Banner, value: any) => void = (field, value) => {
    if (editingBanner) {
      setEditingBanner({
        ...editingBanner,
        [field]: value,
      });
    }
  };

  // Load more banners
  const loadMore = () => {
    if (lastEvaluatedKey) {
      fetchBanners(lastEvaluatedKey);
    }
  };

  // Handle image load errors
  const handleImageError = (bannerId: string) => {
    setImageLoadErrors((prev) => ({
      ...prev,
      [bannerId]: true,
    }));
    console.error(`Failed to load image for banner ID: ${bannerId}`);
  };

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Banner List ({banners.length})</h2>
        <div className="d-flex gap-2">
          <Button variant="success" as="a" href="/site-management/banner/upload">
            Upload New Banner
          </Button>
          <Button variant="info" onClick={handleManageOrder} disabled={banners.length === 0}>
            Manage Order
          </Button>
          <Button variant="primary" onClick={() => fetchBanners()}>
            Refresh Banners
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <BannerFilters
        showActiveOnly={showActiveOnly}
        setShowActiveOnly={setShowActiveOnly}
        totalBanners={banners.length}
      />

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center">Loading...</div>
          ) : banners.length === 0 ? (
            <div className="text-center">No banners found</div>
          ) : (
            <>
              <BannerList
                banners={banners}
                imageLoadErrors={imageLoadErrors}
                handleImageError={handleImageError}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleImagePreview={handleImagePreview}
              />

              {lastEvaluatedKey && (
                <div className="text-center mt-3">
                  <Button variant="outline-primary" onClick={loadMore}>
                    Load More
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Media Preview Modal */}
      <MediaPreviewModal
        show={showPreviewModal}
        onHide={closePreviewModal}
        imageUrl={previewImage}
        title={previewTitle}
      />

      {/* Edit Banner Modal */}
      <EditBannerModal
        show={showEditModal}
        onHide={handleCloseModal}
        editingBanner={editingBanner}
        handleInputChange={handleInputChange}
        handleSave={handleSaveEdit}
      />

      {/* Banner Order Modal */}
      <BannerOrderModal
        show={showOrderModal}
        onHide={() => setShowOrderModal(false)}
        banners={banners}
        onSaveOrder={handleSaveOrder}
        isLoading={isUpdatingOrder}
      />
    </div>
  );
}
